import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgbTabset } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';

import {
  AccountCategory,
  AccountCategoryCode,
  AccountSettings,
  AccountSettingsPayload,
  AccountState,
  DkimDomain,
  EmailQuota,
  IAccount,
  LicensePackage,
  PatchSmsCreditsPayload,
  Permission,
  ProAccount,
  SmsCredits,
} from '@account-users/account/models';
import { LicenseV2 } from '@account-users/account/state/feature-toggles/models';

import { IMigration } from '@shared';

@Component({
  selector: 'app-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSettingsComponent implements OnInit, OnChanges {
  @HostBinding('class.disabled') @Input() disabled: boolean;
  @Input() set accountDetails(details: IAccount) {
    this._accountDetails = details;
  }
  @Input() hasOrderManagerRole: boolean;
  @Input() isUpdatingAudiencePackageSize: boolean;
  @Input() isUpdatingUserSeats: boolean;
  @Input() isUpdatingSectionsLimit: boolean;
  @Input() accountSettings: AccountSettings;
  @Input() allLicenses: LicenseV2[];
  @Input() isLoadingAccountSettings: boolean;
  @Input() loadingProAccountIds: boolean;
  @Input() proAccounts: ProAccount[];
  @Input() proAccountsIsLoading: boolean;
  @Input() accountState: AccountState;
  @Input() currentSmsCredits: SmsCredits;
  @Input() isUpdatingSmsCredits = false;
  @Input() isLoadingSmsCredits = false;
  @Input() accountCategories: AccountCategory[] = [];
  @Input() userPermissions: Permission[] = [];
  @Input() mappedProIds: string[] = [];
  @Input() proSyncMigrations: IMigration[] = [];
  @Input() isLoadingMigrations = false;
  @Input() licensePackages: LicensePackage[] = [];
  @Input() isLoadingLicensePackages: boolean;
  @Input() licensePackagesError: boolean;
  @Input() licenseBootstrapState: AccountState;
  @Input() lastUserLoginTime: string;
  @Input() isCrmKeyspaceCreating: boolean;
  @Input() isCrmKeyspaceTimeoutFailed: boolean;
  @Input() rebootstrapLicensesIsLoading: boolean;
  @Input() rebootstrapLicensesTimeoutFailed: boolean;
  @Input() emailQuota: EmailQuota | undefined;
  @Input() emailMonthlyQuotaIsLoading: boolean;
  @Input() emailOneTimeQuotaIsLoading: boolean;
  @Input() expirationDate: string;
  @Input() expirationDateIsLoading: boolean;
  @Input() upgradeAccountInProgress: boolean;
  @Input() updateAccountInProgress: boolean;
  @Input() dkimDomains: DkimDomain;
  @Input() hasSmsCreditSubscription: boolean;

  @Output() showLicenses = new EventEmitter<void>();
  @Output() editAccountSettings = new EventEmitter<AccountSettingsPayload>();
  @Output() proAccountNewLink = new EventEmitter<void>();
  @Output() proAccountDeleteLink = new EventEmitter<void>();
  @Output() updateSmsCredits = new EventEmitter<PatchSmsCreditsPayload>();
  @Output() navigateToMigration = new EventEmitter<void>();
  @Output() createCrmKeyspace = new EventEmitter<void>();
  @Output() rebootstrapLicenses = new EventEmitter<void>();
  @Output() addQuota = new EventEmitter<{
    quota: Partial<EmailQuota>;
    orderNumber: number;
  }>();
  @Output() addExpirationDate = new EventEmitter<{
    date: string;
    orderNumber: number;
  }>();
  @Output() upgradeTrial = new EventEmitter<{
    accountId: string;
    subscriptionId: string;
    orderNumber: string;
    licensePackage: string;
  }>();
  @Output() updateAccount = new EventEmitter<IAccount>();
  @Output() updateDkimDomains = new EventEmitter<DkimDomain>();

  get accountDetails() {
    return this._accountDetails;
  }

  AccountState = AccountState;
  editAccountMessage: string;
  editButtonText = 'Edit';
  edit = false;
  usersErrorTextMin = "Allowed seats can't be less than zero";
  audienceErrorTextMin = "Number of profiles can't be less than zero";
  sectionsErrorTextMin = "Number of sections can't be less than zero";
  _accountDetails: IAccount = null;

  @ViewChild('tabs')
  tabs: NgbTabset;

  private destroy$: Subject<void> = new Subject<void>();

  public readonly sharePointUrl =
    'https://efficy.sharepoint.com/:b:/s/ApsisRnD/EZXqje6oSBpFrABHow_3AuUBNOhIzhnzejASBUcW3k8UAw?e=zefoZx';

  constructor() {}

  public get licensePackage() {
    return this.licensePackages.find(
      (p) => p.code === this.accountDetails.licensePackage,
    );
  }

  public get canPlaceOrders() {
    return this.userPermissions.includes(Permission.CanPlaceOrders);
  }

  public get canCreateCrmKeyspace() {
    return this.userPermissions.includes(Permission.CanCreateCrmKeyspace);
  }

  public get hasWawBackOffice() {
    return this.userPermissions.includes(Permission.WawBackOfficePreLaunch);
  }

  public get isTrial() {
    return this.accountDetails.category === AccountCategoryCode.Trial;
  }

  ngOnInit() {
    this.editAccountMessage = this.canPlaceOrders
      ? null
      : 'You do not have required permission to edit account details';
  }

  ngOnChanges(changes: SimpleChanges) {
    const { updateAccountInProgress } = changes;
    if (updateAccountInProgress && !updateAccountInProgress.firstChange) {
      this.edit = false;
    }
  }

  public editDetails(e: Event) {
    e.preventDefault();
    this.edit = true;
  }
}
